import React from 'react';
import { Trash2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';

/**
 * ProductEditTable - Formulaire d'édition de produits RÉUTILISABLE
 * 
 * Utilisé par:
 * - ProductImportModal (import multi-produits)
 * - InvoiceImportDialog (ajout produits depuis facture)
 * 
 * Garantit exactement le même formulaire partout.
 */
export const ProductEditTable = ({ 
  products, 
  onProductUpdate, 
  onProductRemove,
  minProducts = 1,
  showVerification = false,
  showDlc = false,
  typeOptions = null,
  rayonOptions = null,
  unitOptions = null,
  packagingTypeOptions = null,
  selectable = false,
  selectedIds = null,
  onToggleSelect = null,
  onToggleSelectAll = null
}) => {
  const DEFAULT_UNITS = ['KG','L','G','ML','U','BTE','PQT','CART','SAC','BQT','RLX','BID'];
  const DEFAULT_TYPES = ['colis','carton','pièce','sac'];
  const unitList = (unitOptions && unitOptions.length) ? unitOptions.map(o => o.value || o) : DEFAULT_UNITS;
  const typeList = (packagingTypeOptions && packagingTypeOptions.length) ? packagingTypeOptions.map(o => o.value || o) : DEFAULT_TYPES;
  const allSelected = selectable && products.length > 0 && products.every(p => selectedIds?.has(p.id));
  return (
    <div className="border rounded-lg overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead className="bg-gray-50">
          <tr>
            {selectable && (
              <th className="p-1 border text-center">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={(e) => onToggleSelectAll?.(e.target.checked)}
                  title="Tout sélectionner"
                  data-testid="select-all-products"
                />
              </th>
            )}
            <th className="p-1 border text-left text-xs font-medium text-gray-700">#</th>
            {showVerification && (
              <th className="p-1 border text-center text-xs font-medium text-gray-700">Vérif</th>
            )}
            <th className="p-1 border text-left text-xs font-medium text-gray-700">Code*</th>
            <th className="p-1 border text-left text-xs font-medium text-gray-700">Article*</th>
            <th className="p-1 border text-left text-xs font-medium text-gray-700">Type</th>
            <th className="p-1 border text-left text-xs font-medium text-gray-700">Rayon</th>
            {showDlc && (
              <th className="p-1 border text-center text-xs font-medium text-gray-700">DLC</th>
            )}
            <th className="p-1 border text-left text-xs font-medium text-gray-700">Conditionnement</th>
            <th className="p-1 border text-left text-xs font-medium text-gray-700">Prix colis (€)</th>
            <th className="p-1 border text-center text-xs font-medium text-gray-700">Détaillable</th>
            <th className="p-1 border text-left text-xs font-medium text-gray-700">Unité détaillable</th>
            <th className="p-1 border text-left text-xs font-medium text-gray-700">Prix catalogue</th>
            <th className="p-1 border text-center text-xs font-medium text-gray-700">Actions</th>
          </tr>
        </thead>
        <tbody>
          {products.map((product, index) => (
            <tr key={product.id} className={`${!product.code || !product.article ? 'bg-red-50' : selectable && selectedIds?.has(product.id) ? 'bg-emerald-50/60' : ''}`}>
              {selectable && (
                <td className="p-1 border text-center">
                  <input
                    type="checkbox"
                    checked={selectedIds?.has(product.id) || false}
                    onChange={() => onToggleSelect?.(product.id)}
                    data-testid={`select-product-${index}`}
                  />
                </td>
              )}
              <td className="p-1 border text-center text-xs text-gray-500">{index + 1}</td>
              
              {showVerification && (
                <td className="p-1 border text-center" data-testid={`verif-badge-${index}`}>
                  {product._ocr?.verifie ? (
                    <span
                      className="inline-block text-green-600 font-bold"
                      title={`Ligne vérifiée arithmétiquement${product._ocr?.conditionnement_brut ? ` — Conditionnement facture : ${product._ocr.conditionnement_brut}` : ''}`}
                    >✓</span>
                  ) : (
                    <span
                      className="inline-block text-orange-500 font-bold cursor-help"
                      title={product._ocr?.note || 'Prix × quantité ≠ total ligne — à contrôler manuellement'}
                    >⚠</span>
                  )}
                </td>
              )}
              
              <td className="p-1 border">
                <Input
                  value={product.code}
                  onChange={(e) => onProductUpdate(product.id, 'code', e.target.value)}
                  placeholder="Code"
                  className="h-9 text-sm"
                />
              </td>
              
              <td className="p-1 border">
                <Input
                  value={product.article}
                  onChange={(e) => onProductUpdate(product.id, 'article', e.target.value)}
                  placeholder="Article"
                  className="h-9 text-sm"
                />
              </td>
              
              <td className="p-1 border">
                {typeOptions ? (
                  <Select
                    value={product.product_type || undefined}
                    onValueChange={(value) => onProductUpdate(product.id, 'product_type', value)}
                  >
                    <SelectTrigger className="h-9 text-xs w-28" data-testid={`type-select-${index}`}>
                      <SelectValue placeholder="Type" />
                    </SelectTrigger>
                    <SelectContent>
                      {typeOptions.map(t => (
                        <SelectItem key={t.id} value={t.id}>{t.nom}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    value={product.product_type}
                    onChange={(e) => onProductUpdate(product.id, 'product_type', e.target.value)}
                    placeholder="Type"
                    className="h-9 text-sm"
                  />
                )}
              </td>
              
              <td className="p-1 border">
                {rayonOptions ? (
                  <Select
                    value={product.rayon || undefined}
                    onValueChange={(value) => onProductUpdate(product.id, 'rayon', value)}
                  >
                    <SelectTrigger className="h-9 text-xs w-32" data-testid={`rayon-select-${index}`}>
                      <SelectValue placeholder="Rayon" />
                    </SelectTrigger>
                    <SelectContent>
                      {rayonOptions.map(r => (
                        <SelectItem key={r.id} value={r.id}>{r.nom}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    value={product.rayon}
                    onChange={(e) => onProductUpdate(product.id, 'rayon', e.target.value)}
                    placeholder="Rayon"
                    className="h-9 text-sm"
                  />
                )}
              </td>
              
              {showDlc && (
                <td className="p-1 border text-center">
                  <Switch
                    checked={Boolean(product.dlc)}
                    onCheckedChange={(checked) => onProductUpdate(product.id, 'dlc', checked)}
                    className="data-[state=checked]:bg-amber-500"
                    data-testid={`dlc-switch-${index}`}
                  />
                </td>
              )}
              
              <td className="p-1 border">
                <div className="flex items-center gap-1">
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={product.supplier_packaging_quantity}
                    onChange={(e) => {
                      const value = e.target.value.replace(',', '.');
                      onProductUpdate(product.id, 'supplier_packaging_quantity', value);
                    }}
                    placeholder="10"
                    className="h-9 text-sm w-14 text-center"
                  />
                  <Select
                    value={product.supplier_packaging_unit}
                    onValueChange={(value) => onProductUpdate(product.id, 'supplier_packaging_unit', value)}
                  >
                    <SelectTrigger className="h-9 text-xs w-16">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {unitList.map((v) => (
                        <SelectItem key={`u-${v}`} value={v}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={product.supplier_packaging_type}
                    onValueChange={(value) => onProductUpdate(product.id, 'supplier_packaging_type', value)}
                  >
                    <SelectTrigger className="h-9 text-xs w-20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {typeList.map((v) => (
                        <SelectItem key={`t-${v}`} value={v}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </td>
              
              <td className="p-1 border">
                <Input
                  type="text"
                  inputMode="decimal"
                  value={product.supplier_package_price}
                  onChange={(e) => {
                    const value = e.target.value.replace(',', '.');
                    onProductUpdate(product.id, 'supplier_package_price', value);
                  }}
                  placeholder="0.00"
                  className="h-9 text-sm text-right"
                />
              </td>
              
              <td className="p-1 border text-center">
                <Switch
                  checked={product.is_divisible}
                  onCheckedChange={(checked) => {
                    onProductUpdate(product.id, 'is_divisible', checked);
                  }}
                  className="data-[state=checked]:bg-green-600"
                />
              </td>
              
              <td className="p-1 border">
                <div className="flex items-center gap-1">
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={product.divisible_quantity}
                    onChange={(e) => {
                      const value = e.target.value.replace(',', '.');
                      onProductUpdate(product.id, 'divisible_quantity', value);
                    }}
                    placeholder="1"
                    className="h-9 text-sm w-12 text-center"
                    disabled={!product.is_divisible}
                  />
                  <Select
                    value={product.divisible_unit}
                    onValueChange={(value) => {
                      onProductUpdate(product.id, 'divisible_unit', value);
                    }}
                    disabled={!product.is_divisible}
                  >
                    <SelectTrigger className="h-9 text-xs w-16">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {unitList.map((v) => (
                        <SelectItem key={`d2-${v}`} value={v}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </td>
              
              <td className="p-1 border">
                <Input
                  type="text"
                  inputMode="decimal"
                  value={product.catalog_price}
                  onChange={(e) => {
                    const value = e.target.value.replace(',', '.');
                    onProductUpdate(product.id, 'catalog_price', value);
                  }}
                  placeholder="Auto"
                  className="h-9 text-sm text-right font-semibold"
                />
              </td>
              
              <td className="p-1 border text-center">
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => onProductRemove(product.id)}
                  disabled={products.length <= minProducts}
                  className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ProductEditTable;
